import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getAiAssessmentAccess, getAiAssessments } from "../utils/utils";
import { appendAssessmentPage, buildAssessmentQuery } from "./assessmentView";

const emptyProtected = {
  items: [], nextCursor: null, coverage: null, detail: null, selectedId: null, pendingPage: null,
};

function stateForError(error) {
  if (error?.status === 401) return "signedout";
  if (error?.status === 403) return "locked";
  if (error?.status === 503) return "unavailable";
  return null;
}

export default function useAiAssessments({ authenticated, filters }) {
  const query = useMemo(() => buildAssessmentQuery(filters), [filters]);
  const [accessStatus, setAccessStatus] = useState(authenticated ? "checking" : "signedout");
  const [data, setData] = useState(emptyProtected);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generation, setGeneration] = useState(0);
  const listRequestRef = useRef(0);
  const refreshModeRef = useRef(false);
  const previousSession = useRef(authenticated);

  const clearProtected = useCallback(() => setData(emptyProtected), []);

  useEffect(() => {
    if (previousSession.current !== authenticated) {
      previousSession.current = authenticated;
      clearProtected();
    }
    if (!authenticated) {
      setAccessStatus("signedout");
      setError(null);
      clearProtected();
      return undefined;
    }
    const controller = new AbortController();
    const request = ++listRequestRef.current;
    const manualRefresh = refreshModeRef.current;
    refreshModeRef.current = false;
    setAccessStatus((current) => current === "allowed" ? "allowed" : "checking");
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const access = await getAiAssessmentAccess({ signal: controller.signal });
        if (request !== listRequestRef.current) return;
        if (!access?.allowed) {
          setAccessStatus("locked");
          clearProtected();
          return;
        }
        setAccessStatus("allowed");
        const page = await getAiAssessments(query, { signal: controller.signal });
        if (request !== listRequestRef.current) return;
        setData((current) => ({
          ...current,
          ...(manualRefresh && current.selectedId && current.items.length > 0 ? {
            pendingPage: page,
          } : {
            items: Array.isArray(page.items) ? page.items : [],
            nextCursor: page.nextCursor || null,
            coverage: page.coverage || null,
            pendingPage: null,
          }),
        }));
      } catch (requestError) {
        if (request !== listRequestRef.current || controller.signal.aborted) return;
        const status = stateForError(requestError);
        if (status) {
          setAccessStatus(status);
          clearProtected();
        } else {
          setAccessStatus("allowed");
          setError(requestError);
        }
      } finally {
        if (request === listRequestRef.current) setLoading(false);
      }
    })();
    return () => controller.abort();
  }, [authenticated, query, generation, clearProtected]);

  const retry = useCallback(() => {
    refreshModeRef.current = true;
    setGeneration((value) => value + 1);
  }, []);

  const loadMore = useCallback(async () => {
    if (!data.nextCursor || loading || accessStatus !== "allowed") return false;
    const controller = new AbortController();
    const request = ++listRequestRef.current;
    setLoading(true);
    setError(null);
    try {
      const page = await getAiAssessments({ ...query, cursor: data.nextCursor }, { signal: controller.signal });
      if (request !== listRequestRef.current) return;
      setData((current) => ({
        ...current,
        items: appendAssessmentPage(current.items, page.items),
        nextCursor: page.nextCursor || null,
        coverage: page.coverage || current.coverage,
      }));
      return true;
    } catch (requestError) {
      const status = stateForError(requestError);
      if (status) {
        setAccessStatus(status);
        clearProtected();
      } else setError(requestError);
      return false;
    } finally {
      if (request === listRequestRef.current) setLoading(false);
    }
  }, [accessStatus, clearProtected, data.nextCursor, loading, query]);

  const select = useCallback((id) => {
    if (!id || accessStatus !== "allowed") return;
    setData((current) => ({ ...current, selectedId: id, detail: null }));
  }, [accessStatus]);

  const closeDetail = useCallback(() => setData((current) => ({ ...current, selectedId: null, detail: null })), []);

  const applyNewResults = useCallback(() => setData((current) => current.pendingPage ? ({
    ...current,
    items: Array.isArray(current.pendingPage.items) ? current.pendingPage.items : [],
    nextCursor: current.pendingPage.nextCursor || null,
    coverage: current.pendingPage.coverage || null,
    pendingPage: null,
  }) : current), []);

  const protectedError = useCallback((requestError) => {
    const status = stateForError(requestError);
    if (!status) return;
    setAccessStatus(status);
    clearProtected();
  }, [clearProtected]);

  return {
    accessStatus, ...data, hasNewResults: Boolean(data.pendingPage), loading, error,
    retry, loadMore, select, closeDetail, applyNewResults, protectedError,
  };
}
